'use client';

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Grid3x3,
  List,
  Play,
  Trash2,
  Edit2,
  MoreVertical,
  Workflow,
  Terminal,
  GitBranch,
  RefreshCw,
  Eye,
  X,
  Sparkles,
} from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import type {
  Workflow as WorkflowType,
  WorkflowCategory,
  WorkflowStatus,
  Execution,
} from './automationTypes';
import { getCategoryConfig, getTriggerConfig } from './automationConstants';
import { automationAPI } from './automationAPI';

// ==================== Components ====================

interface ExecutionHistoryProps {
  workflowId: string;
}

const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({ workflowId }) => {
  const { t } = useTranslation();
  const { data: executions = [], isLoading } = useQuery({
    queryKey: ['executions', workflowId],
    staleTime: 60_000,
    queryFn: async () => {
      const res = await automationAPI.getExecutions(workflowId);
      const items = res.data?.executions ?? [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return items.map((e: any) => ({
        ...e,
        startedAt: e.startedAt || e.started_at,
        completedAt: e.completedAt || e.completed_at,
        duration: e.duration ?? e.duration_ms ?? 0,
        actions: e.actions || e.action_results || [],
      } as Execution));
    },
  });

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <RefreshCw size={16} className="text-gray-400 mx-auto animate-spin" />
      </div>
    );
  }

  if (!Array.isArray(executions) || executions.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">{t('auto_no_executions', 'No executions yet')}</p>;
  }

  return (
    <div className="space-y-2">
      {(Array.isArray(executions) ? executions : []).slice(0, 5).map((execution) => (
        <div key={execution.id} className="flex items-center gap-2 text-xs py-1">
          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              (execution.actions || []).every((a) => a.status === 'completed')
                ? 'bg-emerald-500'
                : 'bg-red-500'
            }`}
          />
          <span className="text-gray-600 flex-1">
            {new Date(execution.startedAt).toLocaleString()}
          </span>
          <span className="text-gray-500">{execution.duration}ms</span>
        </div>
      ))}
    </div>
  );
};

interface WorkflowCardProps {
  workflow: WorkflowType;
  onEdit: (workflow: WorkflowType) => void;
  onToggleActive: (id: string, active: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onTest: (id: string) => Promise<void>;
}

const WorkflowCard = React.forwardRef<HTMLDivElement, WorkflowCardProps>(({
  workflow,
  onEdit,
  onToggleActive,
  onDelete,
  onTest,
}, ref) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showExecutions, setShowExecutions] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const categoryConfig = useMemo(() => getCategoryConfig(t)[workflow.category], [t, workflow.category]);
  const triggerConfig = useMemo(() => getTriggerConfig(t), [t]);

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await onToggleActive(workflow.id, workflow.status === 'inactive');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('auto_confirm_delete', 'Are you sure you want to delete this workflow?'))) {
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete(workflow.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    try {
      await onTest(workflow.id);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group border border-slate-200/60 rounded-xl overflow-hidden transition-colors bg-white"
      style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
    >
      {/* Header */}
      <div className="p-3 border-b border-slate-100/50 flex items-start justify-between bg-white">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`${categoryConfig.color} text-white rounded p-1.5`}>
              {categoryConfig.icon}
            </div>
            <span className="text-[10px] font-semibold text-gray-600 px-2 py-0.5 rounded-full bg-gray-100">
              {categoryConfig.label}
            </span>
          </div>
          <h3 className="text-sm font-bold text-gray-900 mb-0.5 line-clamp-1">{workflow.name}</h3>
          <p className="text-xs text-gray-500 line-clamp-1">{workflow.description || t('auto_no_desc', 'No description')}</p>
        </div>
        <div className="relative flex-shrink-0 ml-3">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-200/50 rounded-lg transition-all duration-200 text-gray-600 hover:text-gray-900 opacity-0 group-hover:opacity-100"
          >
            <MoreVertical size={18} />
          </button>

          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-sm z-10 min-w-max"
              >
                <button
                  onClick={() => {
                    onEdit(workflow);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 hover:bg-emerald-50 text-gray-900 text-sm flex items-center gap-2 transition-colors first:rounded-t-md font-semibold"
                >
                  <Edit2 size={14} />
                  {t('auto_edit', 'Edit')}
                </button>
                <button
                  onClick={() => {
                    handleTest();
                    setShowMenu(false);
                  }}
                  disabled={isTesting}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-gray-900 text-sm flex items-center gap-2 transition-colors font-medium disabled:opacity-50"
                >
                  <Play size={14} />
                  {isTesting ? t('auto_testing', 'Testing...') : t('auto_test', 'Test')}
                </button>
                <button
                  onClick={() => {
                    handleDelete();
                    setShowMenu(false);
                  }}
                  disabled={isDeleting}
                  className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-600 text-sm flex items-center gap-2 transition-colors last:rounded-b-md font-medium disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  {isDeleting ? t('auto_deleting', 'Deleting...') : t('auto_delete', 'Delete')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Stats + Config */}
      <div className="px-3 py-2 bg-white">
        <div className="flex items-center gap-3 text-[10px] text-gray-500 mb-2">
          <span className="font-semibold text-emerald-600">{workflow.stats.totalExecutions} runs</span>
          <span>·</span>
          <span>{Math.round(workflow.stats.successRate)}% success</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><Terminal size={10} className="text-emerald-600" />{triggerConfig[workflow.trigger?.type as keyof typeof triggerConfig]?.label ?? 'Manual'}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><GitBranch size={10} className="text-teal-600" />{workflow.actions.length} actions</span>
        </div>
      </div>

      {/* Control Bar */}
      <div className="px-3 py-2 border-t border-gray-100 bg-white flex items-center justify-between">
        <button
          onClick={handleToggle}
          disabled={isToggling}
          className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${
            workflow.status === 'active'
              ? 'bg-emerald-600'
              : 'bg-gray-300'
          } disabled:opacity-50`}
        >
          <span
            className={`inline-block w-4 h-4 transform rounded-full bg-white shadow-sm transition-transform mt-0.5 ${
              workflow.status === 'active' ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>

        <button
          onClick={() => setShowExecutions(!showExecutions)}
          className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1.5 transition-colors"
        >
          <Eye size={13} />
          {showExecutions ? t('auto_hide', 'Hide') : t('auto_view', 'View')} {t('auto_history', 'History')}
        </button>
      </div>

      {/* Execution History - Expandable */}
      <AnimatePresence>
        {showExecutions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-100 px-5 py-4 bg-gray-50/50"
          >
            <ExecutionHistory workflowId={workflow.id} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});
WorkflowCard.displayName = 'WorkflowCard';

// ==================== Main Page ====================

export const AutomationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const pc = theme.hex.primary;
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<WorkflowCategory | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<WorkflowStatus | 'all'>('all');
  const [showTemplates, setShowTemplates] = useState(false);
  const queryClient = useQueryClient();

  const categoryConfig = useMemo(() => getCategoryConfig(t), [t]);

  // Queries
  const { data: workflows = [], isLoading: workflowsLoading } = useQuery({
    queryKey: ['workflows'],
    staleTime: 60_000,
    queryFn: async () => {
      const res = await automationAPI.getWorkflows();
      const items = res.data?.workflows ?? res.data ?? [];
      // Map backend fields to frontend Workflow shape
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (Array.isArray(items) ? items : []).map((w: Record<string, any>) => ({
        ...w,
        status: w.status || (w.is_active ? 'active' : 'inactive'),
        createdAt: w.createdAt || w.created_at,
        updatedAt: w.updatedAt || w.updated_at,
        trigger: w.trigger || { type: w.trigger_type || 'manual', config: {} },
        conditions: w.conditions || [],
        actions: w.actions || [],
        stats: w.stats || {
          totalExecutions: w.total_executions || 0,
          successRate: w.total_executions
            ? ((w.successful_executions || 0) / (w.total_executions as number)) * 100
            : 0,
          lastRunAt: w.last_executed_at || undefined,
        },
      } as WorkflowType));
    },
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    staleTime: 60_000,
    queryFn: async () => {
      const res = await automationAPI.getTemplates();
      const items = res.data?.templates ?? res.data ?? [];
      return Array.isArray(items) ? items : [];
    },
  });

  // Mutations
  const toggleMutation = useMutation({
    mutationFn: async (data: { id: string; active: boolean }) => {
      if (data.active) {
        return automationAPI.activateWorkflow(data.id);
      } else {
        return automationAPI.deactivateWorkflow(data.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: () => {
      toast.error(t('auto_toggle_failed', 'Failed to toggle workflow'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => automationAPI.deleteWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success(t('auto_deleted', 'Workflow deleted'));
    },
    onError: () => {
      toast.error(t('auto_delete_failed', 'Failed to delete workflow'));
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => automationAPI.testWorkflow(id),
    onSuccess: () => {
      toast.success(t('auto_test_triggered', 'Workflow test triggered'));
    },
    onError: () => {
      toast.error(t('auto_test_failed', 'Failed to test workflow'));
    },
  });

  const installTemplateMutation = useMutation({
    mutationFn: (templateId: string) => automationAPI.installTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success(t('auto_template_installed', 'Template installed as new workflow'));
      setShowTemplates(false);
    },
    onError: () => {
      toast.error(t('auto_template_failed', 'Failed to install template'));
    },
  });

  // Filters
  const filteredWorkflows = useMemo(() => {
    return workflows.filter((w) => {
      const matchesSearch = w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || w.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || w.status === selectedStatus;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [workflows, searchQuery, selectedCategory, selectedStatus]);

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="px-4 pt-3 pb-4">

      {/* Compact Toolbar */}
      <div className="flex items-center gap-2 mb-3">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('auto_search', 'Search workflows...')}
            className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all bg-white"
          />
        </div>

        {/* Status filter */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as WorkflowStatus | 'all')}
          className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 bg-white font-medium"
        >
          <option value="all">{t('auto_all_status', 'All Status')}</option>
          <option value="active">{t('auto_active', 'Active')}</option>
          <option value="inactive">{t('auto_inactive', 'Inactive')}</option>
          <option value="draft">{t('auto_draft', 'Draft')}</option>
        </select>

        {/* Category filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value as WorkflowCategory | 'all')}
          className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 bg-white font-medium"
        >
          <option value="all">{t('auto_all_categories', 'All Categories')}</option>
          {Object.entries(categoryConfig).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        {(searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all') && (
          <button
            onClick={() => { setSearchQuery(''); setSelectedCategory('all'); setSelectedStatus('all'); }}
            className="text-xs text-red-600 hover:bg-red-50 px-2 py-1.5 rounded-lg font-medium transition-colors"
          >
            {t('auto_clear', 'Clear')}
          </button>
        )}

        {/* View toggle */}
        <div className="flex gap-px p-0.5 bg-slate-200/60 rounded-lg ml-auto">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Grid3x3 size={14} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <List size={14} />
          </button>
        </div>

        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs font-medium text-gray-700 transition-colors"
        >
          <Sparkles size={14} />
          {t('auto_templates', 'Templates')}
        </button>

        <button
          onClick={() => navigate('/automations/new')}
          className="flex items-center gap-1 px-3 py-1.5 text-white rounded-lg text-xs font-bold transition-colors"
          style={{ backgroundColor: pc, opacity: 1 }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          <Plus size={14} />
          {t('auto_new_workflow', 'New Workflow')}
        </button>
      </div>

      {/* Workflows Grid/List */}
      {workflowsLoading ? (
        <div className="flex flex-col items-center justify-center py-10">
          <RefreshCw size={24} className="text-emerald-400 animate-spin mb-2" />
          <p className="text-gray-500 text-xs">{t('auto_loading', 'Loading workflows...')}</p>
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200/60 text-center py-10 px-6" style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}>
          <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-2">
            <Workflow size={20} className="text-gray-400" />
          </div>
          <h3 className="text-sm font-bold text-gray-900 mb-1">{t('auto_no_workflows', 'No workflows found')}</h3>
          <p className="text-xs text-gray-500 mb-4 max-w-xs mx-auto">
            {searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all'
              ? t('auto_adjust_filters', 'Try adjusting your filters')
              : t('auto_create_first', 'Create your first automation workflow')}
          </p>
          {!(searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all') && (
            <button
              onClick={() => navigate('/automations/new')}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-xs font-semibold transition-colors"
              style={{ backgroundColor: pc, opacity: 1 }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <Plus size={14} />
              {t('auto_create_wf', 'Create Workflow')}
            </button>
          )}
        </div>
      ) : (
        <motion.div
          layout
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'
              : 'space-y-2'
          }
        >
          <AnimatePresence mode="popLayout">
            {filteredWorkflows.map((workflow) => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onEdit={(w) => navigate(`/automations/${w.id}/edit`)}
                onToggleActive={(id, active) => toggleMutation.mutateAsync({ id, active }).then(() => {})}
                onDelete={(id) => deleteMutation.mutateAsync(id).then(() => {})}
                onTest={(id) => testMutation.mutateAsync(id).then(() => {})}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      </div>

      {/* Template Gallery Modal */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTemplates(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col border border-slate-200/60"
            >
              <div className="flex-shrink-0 px-4 py-3 flex items-center justify-between border-b border-slate-200/60 rounded-t-xl">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles size={16} className="text-emerald-600" />
                  {t('auto_templates', 'Templates')}
                </h2>
                <button onClick={() => setShowTemplates(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors">
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 min-h-0 p-4 overflow-y-auto">
                {templatesLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw size={20} className="text-emerald-400 mx-auto animate-spin mb-2" />
                    <p className="text-xs text-gray-500">{t('auto_loading_tpl', 'Loading templates...')}</p>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8">
                    <Workflow size={24} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-xs text-gray-500">{t('auto_no_templates', 'No templates available yet')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {templates.map((template) => {
                      const templateCategoryConfig = categoryConfig[template.category as WorkflowCategory];
                      return (
                        <div
                          key={template.id}
                          className="border border-slate-200/60 rounded-lg p-3 hover:border-emerald-300 transition-colors"
                          style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`${templateCategoryConfig.color} text-white rounded p-1.5`}>
                                {templateCategoryConfig.icon}
                              </div>
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                {templateCategoryConfig.label}
                              </span>
                            </div>
                            <div className="text-xs font-bold text-amber-500">★ {template.rating}</div>
                          </div>

                          <h3 className="font-semibold text-gray-900 text-sm mb-0.5">{template.name}</h3>
                          <p className="text-xs text-gray-500 mb-3 line-clamp-2">{template.description}</p>

                          <button
                            onClick={() => installTemplateMutation.mutate(template.id)}
                            disabled={installTemplateMutation.isPending}
                            className="w-full px-3 py-1.5 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                            style={{ backgroundColor: pc, opacity: 1 }}
                            onMouseEnter={(e) => !installTemplateMutation.isPending && (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                          >
                            {installTemplateMutation.isPending ? t('auto_installing', 'Installing...') : t('auto_install', 'Install')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutomationsPage;
